import React, { useMemo, useState, useEffect } from "react";
import ResearchCard from "../components/ResearchCard";
import { Container } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import LevelBadge from "../components/LevelBadge";
import Button from "../components/Button";
import Dialog from "../components/Dialog";
import usePlayerTalents from "../hooks/usePlayerTalents";
import useToast from "../hooks/useToast";
import {
  getResearchCategoryDescription,
  getResearchCategoryIcon,
  getResearchCategorySlug,
} from "../constants/researchCategories";
import { BuildingApiType, PlayerBuildingState } from "../types/buildings";
import BuildingService from "./village/BuildingService";
import Network from "../utils/Network";
import { setBuildingData } from "../redux/buildings/action";
import { setUserInfo } from "../redux/user/action";
import { useAppTranslation } from "../i18n/useAppTranslation";
import LanguageSwitcher from "../components/LanguageSwitcher";

interface ResearchCategory {
  id: string;
  slug: string;
  title: string;
  image: string;
  description?: string;
  availableCount: number;
}

type Props = {};

const RESEARCH_BUILDING_ID = 7;

const Research = (props: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const openToast = useToast();
  const axios = new Network();
  const { t } = useAppTranslation(["research", "common"]);

  const buildingsData: BuildingApiType[] = useSelector((state: any) => state.buildings);
  const researchBuildingFromStore = buildingsData?.find((b) => b.buildingId === RESEARCH_BUILDING_ID);
  const hasActiveResearchBuilding = !!researchBuildingFromStore
    && researchBuildingFromStore.state !== PlayerBuildingState.NotHave
    && researchBuildingFromStore.state !== PlayerBuildingState.Unavailable;
  const shouldLoadTalents = hasActiveResearchBuilding && (researchBuildingFromStore?.currentLevel ?? 0) >= 1;

  const { flattenedTalents, categories: talentCategories, loading, error, getNextAvailableLevel } = usePlayerTalents({
    enabled: shouldLoadTalents,
  });

  // Research Building state
  const [researchBuilding, setResearchBuilding] = useState<any>(null);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [pendingHashOpen, setPendingHashOpen] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    msg: string;
    submit: (() => Promise<void>) | null;
  }>({ open: false, title: "", msg: "", submit: null });

  // Fetch research building data
  const fetchResearchBuilding = async () => {
    const apiBuilding = buildingsData?.find((b) => b.buildingId === RESEARCH_BUILDING_ID);
    if (apiBuilding && apiBuilding.id) {
      try {
        const response = await BuildingService.getBuildingDetail(apiBuilding.id);
        if ("ok" in response && response.ok && response.data) {
          setResearchBuilding({
            ...apiBuilding,
            ...response.data,
            buildingId: RESEARCH_BUILDING_ID,
          });
        }
      } catch (err) {
        console.error("Failed to fetch research building:", err);
      }
    }
  };

  useEffect(() => {
    if (buildingsData?.length) {
      fetchResearchBuilding();
    }
  }, [buildingsData]);

  useEffect(() => {
    if (location.hash === "#upgrade") {
      setPendingHashOpen(true);
    }
  }, [location.hash]);

  useEffect(() => {
    if (pendingHashOpen && researchBuilding) {
      handleOpenUpgradeDialog();
      setPendingHashOpen(false);
    }
  }, [pendingHashOpen, researchBuilding]);

  const researchBuildingLevel = useMemo(() => {
    if (researchBuilding?.currentLevel !== undefined) {
      return researchBuilding.currentLevel;
    }
    const researchBuildingFromStore = buildingsData?.find((b) => b.buildingId === RESEARCH_BUILDING_ID);
    return researchBuildingFromStore?.currentLevel ?? 0;
  }, [researchBuilding, buildingsData]);

  // Refresh data helpers
  const refreshBuildings = async () => {
    try {
      const response = await BuildingService.getAllBuildings();
      if (response.success && response.data) {
        dispatch(setBuildingData(response.data));
      }
    } catch (err) {
      console.error("Failed to refresh buildings:", err);
    }
  };

  const refreshUserInfo = async () => {
    try {
      const response = await axios.CallApi({ url: "/player", method: "get" });
      if (response.ok) {
        dispatch(setUserInfo(response.data));
      }
    } catch (err) {
      console.error("Failed to refresh user info:", err);
    }
  };

  // Handle upgrade button click
  const handleOpenUpgradeDialog = () => {
    if (researchBuilding) {
      setUpgradeDialogOpen(true);
    }
  };

  const handleCloseUpgradeDialog = () => {
    setUpgradeDialogOpen(false);
    setActiveTab(0);
    if (location.hash === "#upgrade") {
      navigate("/research", { replace: true });
    }
  };

  // Handle upgrade confirmation
  const handleUpgrade = async () => {
    if (!researchBuilding?.id) return;

    setConfirmDialog({
      open: true,
      title: t("research:confirm.upgradeTitle"),
      msg: t("research:confirm.upgradeMessage", {
        level: (researchBuilding.currentLevel || 0) + 1,
      }),
      submit: async () => {
        setUpgradeLoading(true);
        try {
          const response = await BuildingService.upgradeBuilding(researchBuilding.id);
          openToast(response);
          if ("ok" in response && response.ok) {
            setConfirmDialog((prev) => ({ ...prev, open: false }));
            setUpgradeDialogOpen(false);
            await Promise.all([refreshBuildings(), refreshUserInfo()]);
            fetchResearchBuilding();
          }
        } catch (err) {
          console.error("Failed to upgrade research building:", err);
        } finally {
          setUpgradeLoading(false);
        }
      },
    });
  };

  const researchCategories = useMemo<ResearchCategory[]>(() => {
    // If API categories are not loaded yet, show loading state
    if (loading || !talentCategories.length) {
      return [];
    }

    const countAvailableInBranch = (rootId: number) => {
      const descendantIds = getDescendantCategoryIds(rootId);
      const relevantCategoryIds = new Set<number>([rootId, ...descendantIds]);

      return flattenedTalents.filter((talent) => {
        if (!talent.categoryId || !relevantCategoryIds.has(talent.categoryId)) {
          return false;
        }
        const nextLevel = getNextAvailableLevel(talent.talentId);
        const requiredLevel = talent.researchBuildingRequiredLevel ?? 0;
        return !!nextLevel && requiredLevel <= researchBuildingLevel;
      }).length;
    };

    const getDescendantCategoryIds = (rootId: number): number[] => {
      console.log("talentCategories", talentCategories);
      const children = talentCategories.filter((cat) => cat.parentId === rootId);
        
      if (!children.length) {
        return [];
      }

      return children.reduce<number[]>((acc, child) => {
        acc.push(child.id);
        acc.push(...getDescendantCategoryIds(child.id));
        return acc;
      }, []);
    };

    // Filter to only show top-level categories (those without parentId)
    const topLevelCategories = talentCategories.filter(cat => !cat.parentId);

    // Map API categories to research categories
    const categoriesWithData = topLevelCategories.map((apiCategory) => {
      // Determine all categories that belong to this branch (the category itself + descendants)
      const descendantIds = getDescendantCategoryIds(apiCategory.id);
      const relevantCategoryIds = new Set<number>([apiCategory.id, ...descendantIds]);

      // Count talents in this category and its subcategories
      const talentsInCategory = flattenedTalents.filter(
        talent => talent.categoryId ? relevantCategoryIds.has(talent.categoryId) : false
      );

      const fallbackDescription = getResearchCategoryDescription(apiCategory);

      return {
        id: `${apiCategory.id}`,
        slug: getResearchCategorySlug(apiCategory),
        title: apiCategory.name,
        image: getResearchCategoryIcon(apiCategory),
        description: apiCategory.description || fallbackDescription,
        availableCount: countAvailableInBranch(apiCategory.id),
      };
    });

    // Show all top-level categories regardless of talent count
    return categoriesWithData;
  }, [talentCategories, flattenedTalents, loading, getNextAvailableLevel, researchBuildingLevel]);

  const researchLevel = useMemo(() => {
   return researchBuilding?.level
  }, [researchBuilding]);

  const handleCategoryClick = (slug: string) => {
    navigate(`/research/${slug}`);
  };

  return (
    <div className="research-page">
      <Container className="py-4">
        {/* Header Section */}
        <div className="research-header d-flex align-items-center justify-content-between mb-4">
          <div className="d-flex align-items-center gap-3">
            <img
              src="/assets/images/icons/back.svg"
              alt={t("common:actions.back")}
              className="button"
              style={{ width: 53, height: 53 }}
              onClick={() => navigate('/home')}
            />
            <h1 className="research-title dark-yellow fs-1 fw-bold mb-0">
              {t("research:title")}
            </h1>
            <div className="research-level-badge position-relative">
              <LevelBadge
                className="d-flex align-items-center mb-2 justify-content-center fs-bigger bold position-relative"
                style={{
                  width: 60,
                  height: 60,
                }}
                level={researchLevel}
              />
            </div>
          </div>
          <Button
            variant="warning"
            onClick={handleOpenUpgradeDialog}
            disabled={!researchBuilding}
          >
            {t("common:actions.upgrade")}
          </Button>
          <LanguageSwitcher compact className="ms-2" />
        </div>

        {/* Info Section */}
        <div className=" d-flex align-items-center gap-2 mb-4 p-3 rounded-3 ">
          <img
            src="/assets/images/icons/info.png"
            alt={t("common:actions.info")}
            style={{ width: 20, height: 20, marginTop: 2 }}
          />
          <p className="text-white mb-0" style={{ fontSize: 17 }}>
            {t("research:description")}
          </p>
        </div>

        {/* Research Categories Grid */}
        <div className="research-categories-grid">
          {researchCategories.map((category) => (
            <ResearchCard
              key={category.slug}
              title={category.title}
              image={category.image}
              availableCount={category.availableCount}
              onClick={() => handleCategoryClick(category.slug)}
            />
          ))}
        </div>

        {!loading && !researchCategories.length && (
          <div className="text-center text-white opacity-75 py-5">
            {t("research:empty.noTalents")}
          </div>
        )}

        {loading && !researchCategories.length && (
          <div className="text-center text-white opacity-75 py-5">
            {t("research:empty.loadingData")}
          </div>
        )}

        {error && (
          <div className="text-center text-warning opacity-75 py-2">
            {error}
          </div>
        )}
      </Container>

      <style>{`
        .research-page {
          min-height: 100vh;
          background: url('/assets/images/backgrounds/dark-bg.jpg');
          background-size: cover;
          background-position: center;
          background-attachment: fixed;
        }

        .research-categories-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 74px 46px;
          max-width: 900px;
          margin: 0 auto;
          padding: 40px 0;
        }

        .research-progression-table {
          overflow-x: auto;
        }

        .research-progression-table table {
          border-collapse: separate;
          border-spacing: 0;
          color: #e8e6e3;
          font-size: 14px;
        }

        .research-progression-table thead th {
          background: rgba(139, 92, 58, 0.3);
          color: #d4af37;
          font-weight: 600;
          padding: 12px 8px;
          text-align: center;
          border-bottom: 2px solid #8b5c3a;
          font-size: 13px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .research-progression-table thead th:first-child {
          border-top-left-radius: 8px;
        }

        .research-progression-table thead th:last-child {
          border-top-right-radius: 8px;
        }

        .research-progression-table tbody td {
          padding: 14px 8px;
          text-align: center;
          border-bottom: 1px solid rgba(139, 92, 58, 0.2);
          background: rgba(20, 20, 30, 0.4);
          font-size: 13px;
        }

        .research-progression-table tbody tr {
          transition: all 0.3s ease;
        }

        .research-progression-table tbody tr:hover {
          background: rgba(139, 92, 58, 0.15);
        }

        .research-progression-table tbody tr.unlocked {
          background: rgba(0, 200, 200, 0.08);
          color: #00e6e6;
        }

        .research-progression-table tbody tr.unlocked:hover {
          background: rgba(0, 200, 200, 0.15);
        }

        .research-progression-table tbody tr.unlocked td {
          border-bottom-color: rgba(0, 200, 200, 0.2);
          font-weight: 500;
        }

        .research-progression-table tbody tr:last-child td {
          border-bottom: none;
        }

        .research-progression-table tbody tr:last-child td:first-child {
          border-bottom-left-radius: 8px;
        }

        .research-progression-table tbody tr:last-child td:last-child {
          border-bottom-right-radius: 8px;
        }

        .progress-bar.cyan {
          background: linear-gradient(90deg, #00d4d4, #00ffff);
        }

        @media (max-width: 768px) {
          .research-progression-table table {
            font-size: 12px;
          }

          .research-progression-table thead th {
            padding: 10px 6px;
            font-size: 11px;
          }

          .research-progression-table tbody td {
            padding: 12px 6px;
            font-size: 12px;
          }

          .research-progression-table tbody td img {
            width: 14px !important;
            height: 14px !important;
          }

          .research-categories-grid {
          grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
            gap: 24px 16px;
            padding: 20px 0;
          }

          .research-header {
            flex-direction: row !important;
            gap: 8px !important;
          }

          .research-header h1 {
            font-size: 1.5rem !important;
          }

          .research-header img {
            width: 40px !important;
            height: 40px !important;
          }

          .research-level-badge .level-badge {
            width: 45px !important;
            height: 45px !important;
            font-size: 0.9rem !important;
          }

          .research-page .container {
            padding-left: 12px !important;
            padding-right: 12px !important;
          }

          .research-page p {
            font-size: 14px !important;
          }

          .research-page .p-3 {
            padding: 12px !important;
          }

          .research-header .btn {
            font-size: 0.85rem !important;
            padding: 6px 16px !important;
            min-width: 80px !important;
            height: 36px !important;
          }

          .research-header .gap-3 {
            gap: 10px !important;
          }
        }

        @media (max-width: 480px) {
          .research-header .btn {
            font-size: 0.75rem !important;
            padding: 5px 12px !important;
            min-width: 70px !important;
            height: 32px !important;
          }

          .research-header .gap-3 {
            gap: 8px !important;
          }
          .research-header h1 {
            font-size: 1.25rem !important;
          }

          .research-header img {
            width: 35px !important;
            height: 35px !important;
          }

          .research-level-badge .level-badge {
            width: 40px !important;
            height: 40px !important;
            font-size: 0.85rem !important;
          }

          .research-categories-grid {
            gap: 16px 12px;
          }
        }

      `}</style>

      {/* Research Building Upgrade Dialog */}
      <Dialog
        open={upgradeDialogOpen}
        onClose={handleCloseUpgradeDialog}
        title={t("research:dialog.title")}
        noFooter
        size="lg"
      >
        {researchBuilding && (
          <div className="row">
            {/* Building image column */}
            <div className="col-12 col-lg-4 d-none d-sm-block">
              <div className="col-12 col-sm-8 offset-sm-2 col-lg-12 offset-lg-0">
                <div className="d-flex justify-content-center">
                  <div className="main-box-item text-light py-5">
                    <div className="mb-title text-center pt-3">
                      <h3 className="h6 fw-bold fs-med">
                        {t("common:labels.level")}
                        <button className="mb-title-btn fw-bold text-light mx-2" name="lblLevel">
                          {researchBuilding.currentLevel || 0}
                        </button>
                      </h3>
                    </div>
                    <div className="mb-body px-4 my-2">
                      <div className="image px-2 text-center">
                        <img
                          src="/assets/images/map/research.png"
                          className="img-fluid my-2"
                          alt={t("research:dialog.title")}
                        />
                      </div>
                    </div>
                    <div className="mb-button text-center">
                      <Button
                        onClick={() => navigate('/research')}
                        variant="rounded"
                        className="form-btn lightbrown-btn mb-4 mx-auto"
                      >
                        <span className="b-text fs-6 px-5">{t("common:actions.open")}</span>
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Building details column */}
            <div className="col-12 col-lg-8">
              {/* Research Queue Progress Bar */}
              <div className="row mb-0 mb-sm-2">
                <div style={{ flex: 1 }} className="col-12 col-sm-8 col-lg-9">
                  <div className="form-processbar fs-med">
                    <div className="mt-3">
                      <label className="d-flex justify-content-between mb-1">
                        <span>{t("research:dialog.researchQueue")}</span>
                        <span>0 / {researchBuilding.currentLevel >= 7 ? 3 : researchBuilding.currentLevel >= 4 ? 2 : 1}</span>
                      </label>
                      <div className="progress wide">
                        <div
                          className="progress-bar progress-bar-striped cyan"
                          style={{ width: '0%' }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tab Switcher */}
              <div className="row mb-2 mb-md-4">
                <div className="col-12 col-sm-7 col-md-7 mb-3 mb-md-0 order-1 order-sm-0">
                  <div className="d-flex">
                    <button
                      onClick={() => setActiveTab(0)}
                      className="theme-btn button"
                      name="upgradeTab"
                    >
                      <img
                        className="img-fluid"
                        src={
                          activeTab === 0
                            ? "/assets/images/buttons/active-tab-btn.png"
                            : "/assets/images/buttons/off-tab-btn.png"
                        }
                      />
                      <div className="b-text">
                        <span className="text-light fs-med">{t("research:dialog.tabs.upgrade")}</span>
                      </div>
                    </button>
                    <button
                      onClick={() => setActiveTab(1)}
                      className="theme-btn button"
                      name="infoTab"
                    >
                      <img
                        className="img-fluid"
                        src={
                          activeTab === 1
                            ? "/assets/images/buttons/active-tab-btn.png"
                            : "/assets/images/buttons/off-tab-btn.png"
                        }
                      />
                      <div className="b-text">
                        <span className="text-light fs-med">{t("research:dialog.tabs.info")}</span>
                      </div>
                    </button>
                  </div>
                </div>
                <div className="col-12 col-sm-5 col-md-5 d-flex justify-content-center align-items-center mb-3 mb-md-0 order-0 order-sm-1">
                  <img className="img-fluid" src="/assets/images/brown-sep.png" />
                </div>
              </div>

              {/* Tab Content */}
              <div className="row">
                <div className="col-12 position-relative">
                  {/* Upgrade Tab */}
                  {activeTab === 0 && (
                    <div className="tab-box-body p-3 p-lg-4 pb-lg-2">
                      <div className="land-info-list text-light fs-small mb-2">
                        <div className="land-info-item d-flex justify-content-between">
                          <div className="title fw-normal">{t("research:dialog.currentLevel")}</div>
                          <div className="value fw-bold">{researchBuilding.currentLevel || 0}</div>
                        </div>
                        <div className="land-info-item d-flex justify-content-between">
                          <div className="title fw-normal">{t("research:dialog.unlockedTalents")}</div>
                          <div className="value fw-bold">
                            {flattenedTalents.filter(t =>
                              !t.researchBuildingRequiredLevel ||
                              t.researchBuildingRequiredLevel <= (researchBuilding.currentLevel || 0)
                            ).length} / {flattenedTalents.length}
                          </div>
                        </div>
                      </div>

                      {researchBuilding.currentLevel < 8 && (
                        <div className="text-center">
                          <div className="d-flex flex-column align-items-center">
                            {researchBuilding.upgradeCost !== undefined && (
                              <div
                                style={{ marginBottom: 8 }}
                                className="d-flex mt-3 gap-2 flex-wrap align-items-end justify-content-center"
                              >
                                <div className="">{t("research:dialog.cost")}</div>
                                <div className="d-flex ms-2 gap-2 flex-wrap align-items-end">
                                  <img src="/assets/images/icons/cleg.png" width={22} height={22} />
                                  {researchBuilding.upgradeCost}
                                </div>
                              </div>
                            )}
                            <Button
                              onClick={handleUpgrade}
                              variant="wide-primary"
                              className="mt-3"
                              loading={upgradeLoading}
                              disabled={upgradeLoading}
                            >
                              <span>{t("research:dialog.upgradeResearch")}</span>
                            </Button>
                          </div>
                        </div>
                      )}

                      {researchBuilding.currentLevel >= 8 && (
                        <div className="text-center py-3">
                          <img src="/assets/images/icons/medal.png" alt="Max Level" width={50} height={50} />
                          <p className="text-success mt-2 mb-0 fw-bold">{t("research:dialog.maxLevelReached")}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Info Tab */}
                  {activeTab === 1 && (
                    <div className="tab-box-body p-3 p-lg-4 pb-lg-2">
                      <div className="research-progression-table">
                        <table className="w-100">
                          <thead>
                            <tr>
                              <th>{t("research:dialog.progression.level")}</th>
                              <th>{t("research:dialog.progression.costCleg")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className={researchBuilding.currentLevel >= 1 ? 'unlocked' : ''}>
                              <td>1</td>
                              <td align="center">
                                <div className="d-flex align-items-center justify-content-center gap-1">
                                  <img src="/assets/images/icons/cleg.png" width={16} height={16} />
                                  1,000
                                </div>
                              </td>
                            </tr>
                            <tr className={researchBuilding.currentLevel >= 2 ? 'unlocked' : ''}>
                              <td>2</td>
                              <td>
                                <div className="d-flex align-items-center justify-content-center gap-1">
                                  <img src="/assets/images/icons/cleg.png" width={16} height={16} />
                                  1,000
                                </div>
                              </td>
                            </tr>
                            <tr className={researchBuilding.currentLevel >= 3 ? 'unlocked' : ''}>
                              <td>3</td>
                              <td>
                                <div className="d-flex align-items-center justify-content-center gap-1">
                                  <img src="/assets/images/icons/cleg.png" width={16} height={16} />
                                  2,000
                                </div>
                              </td>
                            </tr>
                            <tr className={researchBuilding.currentLevel >= 4 ? 'unlocked' : ''}>
                              <td>4</td>
                              <td>
                                <div className="d-flex align-items-center justify-content-center gap-1">
                                  <img src="/assets/images/icons/cleg.png" width={16} height={16} />
                                  4,000
                                </div>
                              </td>
                            </tr>
                            <tr className={researchBuilding.currentLevel >= 5 ? 'unlocked' : ''}>
                              <td>5</td>
                              <td>
                                <div className="d-flex align-items-center justify-content-center gap-1">
                                  <img src="/assets/images/icons/cleg.png" width={16} height={16} />
                                  8,000
                                </div>
                              </td>
                            </tr>
                            <tr className={researchBuilding.currentLevel >= 6 ? 'unlocked' : ''}>
                              <td>6</td>
                              <td>
                                <div className="d-flex align-items-center justify-content-center gap-1">
                                  <img src="/assets/images/icons/cleg.png" width={16} height={16} />
                                  16,000
                                </div>
                              </td>
                            </tr>
                            <tr className={researchBuilding.currentLevel >= 7 ? 'unlocked' : ''}>
                              <td>7</td>
                              <td>
                                <div className="d-flex align-items-center justify-content-center gap-1">
                                  <img src="/assets/images/icons/cleg.png" width={16} height={16} />
                                  32,000
                                </div>
                              </td>
                            </tr>
                            <tr className={researchBuilding.currentLevel >= 8 ? 'unlocked' : ''}>
                              <td>8</td>
                              <td>
                                <div className="d-flex align-items-center justify-content-center gap-1">
                                  <img src="/assets/images/icons/cleg.png" width={16} height={16} />
                                  64,000
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </Dialog>

      {/* Confirm Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
        title={confirmDialog.title}
        closeLabel={t("common:actions.no")}
        submitLabel={t("common:actions.yes")}
        onSubmit={() => confirmDialog.submit?.()}
        loading={upgradeLoading}
      >
        <p className="text-white text-center">{confirmDialog.msg}</p>
      </Dialog>
    </div>
  );
};

export default Research;
